import axios from 'axios'
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

// Create a private axios instance for auth requests to avoid circular dependencies with @/services/api
const authApi = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:8000/api',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
})

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref(null)
  const accessToken = ref(localStorage.getItem('access_token'))
  const refreshToken = ref(localStorage.getItem('refresh_token'))
  const initialized = ref(false)
  const loading = ref(false)
  const error = ref(null)

  // Getters
  const isAuthenticated = computed(() => !!accessToken.value && !!user.value)
  const isAdmin = computed(() => user.value?.is_admin || user.value?.is_staff || user.value?.is_superuser || false)
  const userFullName = computed(() => {
    if (!user.value) return ''
    return user.value.full_name || user.value.username
  })

  // Actions
  const setTokens = (tokens) => {
    accessToken.value = tokens.access
    refreshToken.value = tokens.refresh
    localStorage.setItem('access_token', tokens.access)
    localStorage.setItem('refresh_token', tokens.refresh)
  }

  const clearTokens = () => {
    accessToken.value = null
    refreshToken.value = null
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
  }

  const initialize = async () => {
    if (initialized.value) return
    
    if (accessToken.value) {
      try {
        // Verify token and get user info - import api lazily to avoid circular dependency
        const { default: api } = await import('@/services/api')
        const response = await api.get('/accounts/profile/')
        user.value = response.data
      } catch (err) {
        // Token might be expired, try to refresh
        if (refreshToken.value) {
          try {
            // Use authApi (no interceptors) for refresh to avoid loops
            const response = await authApi.post('/auth/token/refresh/', {
              refresh: refreshToken.value
            })
            setTokens({
              access: response.data.access,
              refresh: refreshToken.value
            })
            // Get user info with new token
            const { default: api } = await import('@/services/api')
            const userResponse = await api.get('/accounts/profile/')
            user.value = userResponse.data
          } catch (refreshErr) {
            clearTokens()
          }
        } else {
          clearTokens()
        }
      }
    }
    
    initialized.value = true
  }

  const login = async (credentials) => {
    loading.value = true
    error.value = null
    
    try {
      // Use authApi for login
      const tokenResponse = await authApi.post('/auth/token/', credentials)
      setTokens(tokenResponse.data)
      
      // Get user info
      const { default: api } = await import('@/services/api')
      const userResponse = await api.get('/accounts/profile/')
      user.value = userResponse.data
      
      return { success: true }
    } catch (err) {
      error.value = err.response?.data?.detail || 'Login failed. Please try again.'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  const register = async (userData) => {
    loading.value = true
    error.value = null
    
    try {
      await authApi.post('/accounts/register/', userData)
      return { success: true }
    } catch (err) {
      error.value = err.response?.data || 'Registration failed. Please try again.'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  const logout = () => {
    user.value = null
    clearTokens()
  }

  const refreshAccessToken = async () => {
    if (!refreshToken.value) {
      logout()
      throw new Error('No refresh token available')
    }
    
    try {
      const response = await authApi.post('/auth/token/refresh/', {
        refresh: refreshToken.value
      })
      accessToken.value = response.data.access
      localStorage.setItem('access_token', response.data.access)
      return response.data.access
    } catch (err) {
      logout()
      throw err
    }
  }

  const updateProfile = async (profileData) => {
    loading.value = true
    error.value = null
    
    try {
      const { default: api } = await import('@/services/api')
      const response = await api.patch('/accounts/profile/', profileData)
      user.value = { ...user.value, ...response.data }
      return { success: true }
    } catch (err) {
      error.value = err.response?.data || 'Update failed. Please try again.'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  const changePassword = async (passwordData) => {
    loading.value = true
    error.value = null
    
    try {
      await api.post('/accounts/change-password/', passwordData)
      return { success: true }
    } catch (err) {
      error.value = err.response?.data || 'Password change failed. Please try again.'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  return {
    // State
    user,
    accessToken,
    refreshToken,
    initialized,
    loading,
    error,
    // Getters
    isAuthenticated,
    isAdmin,
    userFullName,
    // Actions
    initialize,
    login,
    register,
    logout,
    refreshAccessToken,
    updateProfile,
    changePassword,
    setTokens
  }
})
